// src/pages/Dashboard.jsx
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';
import useCurrency from '../hooks/useCurrency';
import { useFeatures } from '../hooks/useFeatures';
import useViewOnly from '../hooks/useViewOnly';

const LOW_STOCK_THRESHOLD = 5;
const DEFAULT_RATE = 89500;
const GREEN = '#2e9f86';

const TYPE_LABELS = {
  100: 'Purchase In', 120: 'Purchase Return', 200: 'Sales Out',
  220: 'Sales Return', 230: 'Proforma', 300: 'Inventory Count', 400: 'Loss/Damage',
};

const asArray = (d) =>
  Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data
  : Array.isArray(d?.items) ? d.items : Array.isArray(d?.results) ? d.results : [];

const money = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// show an amount in its OWN currency (LBP shown as "… LBP", never converted to $)
const fmtCur = (n, cur) => cur === 'LBP'
  ? `${Math.round(Number(n) || 0).toLocaleString('en-US')} LBP`
  : `$ ${money(n)}`;

const pad = (x) => String(x).padStart(2, '0');
const fmtLocal = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;
const todayLocal = () => { const n = new Date(); return fmtLocal(n.getFullYear(), n.getMonth() + 1, n.getDate()); };
const monthStartLocal = () => { const n = new Date(); return fmtLocal(n.getFullYear(), n.getMonth() + 1, 1); };
const daysAgoLocal = (k) => { const n = new Date(Date.now() - k * 864e5); return fmtLocal(n.getFullYear(), n.getMonth() + 1, n.getDate()); };
// the document's day in the shop's (browser's) time — slicing the UTC date string put sales
// made just after midnight on the previous day
const localDay = (d) => { const x = new Date(d.date); return isNaN(x) ? '' : fmtLocal(x.getFullYear(), x.getMonth() + 1, x.getDate()); };

const docUSD = (doc, field, fallbackRate) => {
  const amt = Number(doc?.totals?.[field] ?? doc?.[field] ?? 0);
  if (!amt) return 0;
  const cur = doc?.currency || 'USD';
  if (cur === 'USD') return amt;
  const r = Number(doc?.exchange_rate_snapshot) || fallbackRate || DEFAULT_RATE;
  return amt / r;
};

const num = (obj, keys) => {
  for (const k of keys) { const v = obj?.[k]; if (v !== undefined && v !== null && v !== '' && !isNaN(Number(v))) return Number(v); }
  return 0;
};

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const viewOnly = useViewOnly();   // MMM Fashion shop: no selling from the web
  const { secCode, rate } = useCurrency();
  const features = useFeatures();
  const dual = !!features.second_currency;

  const today      = todayLocal();
  const monthStart = monthStartLocal();
  const weekAgo    = daysAgoLocal(7);

  const { data: salesMonth   = [], isLoading: l1 } = useQuery(['dash-sales-month', monthStart, today], () =>
    api.get(`/api/documents?type=200&status=confirmed&date_from=${monthStart}&date_to=${today}&all=true`).then((r) => asArray(r.data)));
  // returns this month reduce the sales figures
  const { data: returnsMonth = [] } = useQuery(['dash-returns-month', monthStart, today], () =>
    api.get(`/api/documents?type=220&status=confirmed&date_from=${monthStart}&date_to=${today}&all=true`).then((r) => asArray(r.data)));
  const { data: salesAll     = [], isLoading: l2 } = useQuery(['dash-sales-all'], () =>
    api.get('/api/documents?type=200&status=confirmed&all=true').then((r) => asArray(r.data)));
  const { data: purchasesAll = [], isLoading: l3 } = useQuery(['dash-purch-all'], () =>
    api.get('/api/documents?type=100&status=confirmed&all=true').then((r) => asArray(r.data)));
  const { data: products     = [], isLoading: l4 } = useQuery(['products'], () =>
    api.get('/api/products?all=true').then((r) => asArray(r.data)));
  const { data: recent       = [], isLoading: l5 } = useQuery(['dash-recent', weekAgo, today], () =>
    api.get(`/api/documents?date_from=${weekAgo}&date_to=${today}`).then((r) => asArray(r.data)));
  const { data: accounts     = [] } = useQuery(['accounts'], () =>
    api.get('/api/accounts').then((r) => asArray(r.data)), { staleTime: 60000 });
  const { data: settings } = useQuery(['settings'], () =>
    api.get('/api/settings').then((r) => r.data), { staleTime: 60000 });

  // Onboarding checklist
  const [obDismissed, setObDismissed] = useState(() => localStorage.getItem('mmm_onboarding_v1') === 'done');
  const obSteps = [
    { done: products.length > 0,  labelAr: 'أضف أول منتج',            labelEn: 'Add your first product',         path: '/products' },
    { done: accounts.length > 0,  labelAr: 'أضف عميل أو مورد',         labelEn: 'Add a customer or supplier',      path: '/accounts' },
    { done: salesAll.length > 0,  labelAr: 'أنجز أول عملية بيع',        labelEn: 'Complete your first sale',        path: '/pos' },
    { done: !!settings?.company_name, labelAr: 'أكمل بيانات الشركة',   labelEn: 'Complete company settings',       path: '/settings' },
  ];
  const obAllDone = obSteps.every(s => s.done);
  const obReady   = !l4 && accounts !== undefined; // products + accounts loaded

  const todaySales     = useMemo(() => salesMonth.filter((d) => localDay(d) === today).reduce((s, d) => s + docUSD(d, 'total', rate), 0)
                                     - returnsMonth.filter((d) => localDay(d) === today).reduce((s, d) => s + docUSD(d, 'total', rate), 0), [salesMonth, returnsMonth, today, rate]);
  const todayCollected = useMemo(() => salesMonth.filter((d) => localDay(d) === today).reduce((s, d) => s + docUSD(d, 'paid', rate), 0), [salesMonth, today, rate]);
  const monthSales     = useMemo(() => salesMonth.reduce((s, d) => s + docUSD(d, 'total', rate), 0)
                                     - returnsMonth.reduce((s, d) => s + docUSD(d, 'total', rate), 0), [salesMonth, returnsMonth, rate]);
  const monthCount     = salesMonth.length;
  // this-month sales grouped by the currency each sale was actually billed in (raw)
  const monthByCur     = useMemo(() => {
    const m = {};
    salesMonth.forEach((d) => { const c = d.currency || 'USD'; m[c] = (m[c] || 0) + Number(d.totals?.total || 0); });
    returnsMonth.forEach((d) => { const c = d.currency || 'USD'; m[c] = (m[c] || 0) - Number(d.totals?.total || 0); });
    return Object.entries(m).map(([currency, amount]) => ({ currency, amount }));
  }, [salesMonth, returnsMonth]);
  const receivables    = useMemo(() => salesAll.reduce((s, d) => s + Math.max(0, docUSD(d, 'balance', rate)), 0), [salesAll, rate]);
  const payables       = useMemo(() => purchasesAll.reduce((s, d) => s + Math.max(0, docUSD(d, 'balance', rate)), 0), [purchasesAll, rate]);

  const lowStock = useMemo(() =>
    products
      .filter((p) => (p.manage_stock !== false) && (p.type !== 'variable'))
      .filter((p) => num(p, ['stock']) <= LOW_STOCK_THRESHOLD)
      .sort((a, b) => num(a, ['stock']) - num(b, ['stock'])),
    [products]);

  const recentDocs = useMemo(
    () => [...recent].sort((a, b) => String(b.createdAt || b.date).localeCompare(String(a.createdAt || a.date))).slice(0, 8),
    [recent]);

  const loading = l1 || l2 || l3 || l4 || l5;

  return (
    <div className="p-4 md:p-5 space-y-4 md:space-y-5">
      {/* header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-xl md:text-2xl font-bold text-gray-800">{t('dashboard.title', 'Dashboard')}</h1>
        <div className="flex items-center gap-2 md:gap-3 flex-wrap">
          <span className="text-xs md:text-sm text-gray-500">
            {t('dashboard.rate', 'Rate')}: <b>{Number(rate).toLocaleString()} {secCode}/$</b>
          </span>
          {!viewOnly && (
          <button onClick={() => navigate('/pos')} className="px-3 md:px-4 py-2 rounded text-white font-bold text-sm" style={{ backgroundColor: GREEN }}>
            + {t('dashboard.newSale', 'New Sale')}
          </button>
          )}
        </div>
      </div>

      {/* onboarding checklist */}
      {obReady && !obDismissed && !obAllDone && (
        <OnboardingChecklist
          steps={obSteps}
          onNavigate={navigate}
          onDismiss={() => { localStorage.setItem('mmm_onboarding_v1', 'done'); setObDismissed(true); }}
        />
      )}

      {/* stat cards */}
      <div className="grid gap-3 md:gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        <StatCard label={t('dashboard.salesToday', "Today's Sales")} value={`$ ${money(todaySales)}`}
                  sub={`Collected: $ ${money(todayCollected)}`} accent={GREEN} loading={loading} />
        <StatCard label={t('dashboard.salesMonth', 'This Month')} value={`$ ${money(monthSales)}`}
                  sub={`${monthCount} invoices`} accent="#3b82f6" loading={loading} />
        <StatCard label={t('dashboard.receivables', 'Receivables')} value={`$ ${money(receivables)}`}
                  sub="owed to you" accent="#f59e0b" loading={loading} onClick={() => navigate('/reports')} />
        <StatCard label={t('dashboard.payables', 'Payables')} value={`$ ${money(payables)}`}
                  sub="you owe" accent="#ef4444" loading={loading} onClick={() => navigate('/reports')} />
        <StatCard label={t('dashboard.lowStock', 'Low Stock')} value={`${lowStock.length}`}
                  sub={`≤ ${LOW_STOCK_THRESHOLD} units`} accent="#8b5cf6" loading={loading} onClick={() => navigate('/stock')} />
      </div>

      {/* this-month sales per currency (raw) — shown when the second currency is enabled */}
      {dual && monthByCur.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-3 md:p-4 flex flex-wrap items-center gap-x-6 gap-y-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">{t('dashboard.salesByCurrency', 'This month by currency')}</span>
          {monthByCur.map((c) => (
            <span key={c.currency} className="text-sm font-bold text-gray-800">
              {c.currency}: <span style={{ color: GREEN }}>{fmtCur(c.amount, c.currency)}</span>
            </span>
          ))}
        </div>
      )}

      {/* bottom panels — stack on mobile, side by side on desktop */}
      <div className="grid gap-4 md:gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>

        {/* Recent Documents */}
        <Panel title={t('dashboard.recentDocs', 'Recent Documents')}>
          {recentDocs.length === 0 ? <Empty text={t('dashboard.noRecent', 'No recent documents')} /> : (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table className="w-full text-sm" style={{ minWidth: 420 }}>
                <thead className="text-gray-500 border-b">
                  <tr>
                    <th className="text-start py-2 pr-3">{t('dashboard.number', '#')}</th>
                    <th className="text-start py-2 pr-3">{t('dashboard.type', 'Type')}</th>
                    <th className="text-start py-2 pr-3">{t('dashboard.date', 'Date')}</th>
                    <th className="text-end py-2 pr-3">{t('dashboard.total', 'Total')}</th>
                    <th className="text-center py-2">{t('dashboard.status', 'Status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {recentDocs.map((d) => (
                    <tr key={d._id || d.id} onClick={() => navigate(`/documents/${d._id || d.id}`)}
                        className="border-b last:border-0 hover:bg-gray-50 cursor-pointer">
                      <td className="py-2 pr-3 font-semibold whitespace-nowrap">{d.number || '—'}</td>
                      <td className="py-2 pr-3 whitespace-nowrap text-xs">{TYPE_LABELS[d.type] || d.type}</td>
                      <td className="py-2 pr-3 text-gray-500 whitespace-nowrap">{String(d.date)?.slice(0, 10)}</td>
                      <td className="py-2 pr-3 text-end font-semibold whitespace-nowrap">{fmtCur(d.totals?.total || 0, d.currency)}</td>
                      <td className="py-2 text-center"><StatusBadge status={d.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        {/* Low Stock */}
        <Panel title={`${t('dashboard.lowStock', 'Low Stock')} (${lowStock.length})`}>
          {lowStock.length === 0 ? <Empty text={t('dashboard.stockOk', 'All products well stocked')} /> : (
            <ul className="divide-y">
              {lowStock.slice(0, 10).map((p) => (
                <li key={p._id || p.id} className="flex items-center justify-between py-2 text-sm">
                  <span className="font-medium text-gray-700 truncate mr-3">{p.name}</span>
                  <span className={`font-bold whitespace-nowrap ${num(p, ['stock']) <= 0 ? 'text-red-600' : 'text-amber-600'}`}>
                    {num(p, ['stock'])} {t('dashboard.left', 'left')}
                  </span>
                </li>
              ))}
              {lowStock.length > 10 && (
                <li className="py-2 text-center">
                  <button onClick={() => navigate('/stock')} className="text-sm font-semibold" style={{ color: GREEN }}>
                    {t('dashboard.viewAll', 'View all')} ({lowStock.length})
                  </button>
                </li>
              )}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, accent, loading, onClick }) {
  return (
    <div onClick={onClick}
         className={`bg-white rounded-xl border p-3 md:p-4 ${onClick ? 'cursor-pointer hover:shadow-md transition' : ''}`}
         style={{ borderInlineStartWidth: 4, borderInlineStartColor: accent }}>
      <div className="text-xs md:text-sm text-gray-500">{label}</div>
      <div className="text-lg md:text-2xl font-bold text-gray-800 mt-1">{loading ? '…' : value}</div>
      {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
    </div>
  );
}
function Panel({ title, children }) {
  return (
    <div className="bg-white rounded-xl border p-4">
      <h2 className="font-bold text-gray-700 mb-3">{title}</h2>
      {children}
    </div>
  );
}
function StatusBadge({ status }) {
  const map = { confirmed: 'bg-emerald-100 text-emerald-700', draft: 'bg-gray-100 text-gray-600', cancelled: 'bg-red-100 text-red-700' };
  return <span className={`px-2 py-0.5 rounded text-xs font-semibold ${map[status] || 'bg-gray-100 text-gray-600'}`}>{status}</span>;
}
function Empty({ text }) {
  return <div className="text-center text-gray-400 py-8 text-sm">{text}</div>;
}

function OnboardingChecklist({ steps, onNavigate, onDismiss }) {
  const done  = steps.filter(s => s.done).length;
  const total = steps.length;
  const pct   = Math.round((done / total) * 100);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-5" style={{ borderInlineStartWidth: 4, borderInlineStartColor: GREEN }}>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="font-bold text-gray-800 text-base">إعداد متجرك / Setup your store</div>
          <div className="text-xs text-gray-400 mt-0.5">{done} of {total} steps done</div>
        </div>
        <div className="flex items-center gap-3">
          {/* progress bar */}
          <div className="hidden sm:flex items-center gap-2">
            <div className="w-28 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: GREEN }} />
            </div>
            <span className="text-xs font-semibold text-gray-500">{pct}%</span>
          </div>
          <button onClick={onDismiss} className="text-xs text-gray-400 hover:text-gray-600 underline whitespace-nowrap">
            تخطي / Skip
          </button>
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        {steps.map((s, i) => (
          <button
            key={i}
            onClick={() => !s.done && onNavigate(s.path)}
            disabled={s.done}
            className="flex items-center gap-3 rounded-lg border px-3 py-2.5 text-start transition"
            style={{
              borderColor: s.done ? '#d1fae5' : '#e5e7eb',
              background:  s.done ? '#f0fdf4' : '#fafafa',
              cursor:      s.done ? 'default' : 'pointer',
            }}
          >
            <span className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold"
                  style={{ background: s.done ? '#d1fae5' : '#e5e7eb', color: s.done ? '#065f46' : '#9ca3af' }}>
              {s.done ? '✓' : i + 1}
            </span>
            <div className="min-w-0">
              <div className="text-xs font-semibold text-gray-800 truncate">{s.labelAr}</div>
              <div className="text-[11px] text-gray-400 truncate">{s.labelEn}</div>
            </div>
            {!s.done && (
              <svg className="ms-auto flex-shrink-0 text-gray-300" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}